import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { 
  Heart,
  CreditCard,
  ShoppingCart,
  Store,
  Phone,
  Radio,
  Zap,
  Car,
  GraduationCap,
  Home,
  Factory,
  Truck,
  Clapperboard,
  Package,
  Power
} from "lucide-react";

interface Industry {
  icon: any;
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface IndustriesMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const IndustriesMenu = ({ isOpen, onClose }: IndustriesMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const industries: Industry[] = [
    { 
      icon: Heart, 
      title: "Healthcare", 
      category: "Healthcare",
      description: "Transform healthcare delivery with innovative digital solutions for patient care and medical operations.",
      features: ["Electronic Health Records", "Telemedicine Platforms", "Medical Device Integration", "Patient Management Systems"],
      technologies: ["HIPAA Compliance", "HL7 FHIR", "DICOM", "Epic Integration", "Cerner", "Allscripts"],
      href: "/industries/healthcare"
    },
    { 
      icon: CreditCard, 
      title: "FinTech", 
      category: "Finance",
      description: "Build secure, scalable financial technology solutions that comply with regulatory requirements.",
      features: ["Digital Banking", "Payment Processing", "Blockchain Solutions", "Risk Management"],
      technologies: ["PCI DSS", "SOX Compliance", "Blockchain", "Open Banking APIs", "Core Banking", "RegTech"],
      href: "/industries/fintech"
    },
    { 
      icon: ShoppingCart, 
      title: "E-commerce", 
      category: "Retail",
      description: "Create compelling online shopping experiences with robust e-commerce platforms and solutions.",
      features: ["Multi-vendor Marketplaces", "Payment Gateways", "Inventory Management", "Customer Analytics"],
      technologies: ["Shopify Plus", "WooCommerce", "Magento", "Stripe", "PayPal", "Amazon Web Services"],
      href: "/industries/ecommerce"
    },
    { 
      icon: Store, 
      title: "Retail", 
      category: "Retail",
      description: "Modernize retail operations with omnichannel solutions and customer engagement platforms.",
      features: ["POS Systems", "Omnichannel Experience", "Supply Chain Management", "Customer Loyalty Programs"],
      technologies: ["Salesforce Commerce", "Oracle Retail", "SAP Retail", "Microsoft Dynamics", "Square"],
      href: "/industries/retail"
    },
    { 
      icon: Phone, 
      title: "Telecom", 
      category: "Telecommunications",
      description: "Enhance telecommunications infrastructure with cutting-edge network and customer management solutions.",
      features: ["Network Management", "Billing Systems", "Customer Service Platforms", "IoT Integration"],
      technologies: ["5G", "SDN/NFV", "OSS/BSS", "VoIP", "SIP", "Network Orchestration"],
      href: "/industries/telecom"
    },
    { 
      icon: Radio, 
      title: "Media", 
      category: "Media & Entertainment",
      description: "Deliver engaging digital media experiences with content management and distribution platforms.",
      features: ["Content Management", "Video Streaming", "Digital Publishing", "Audience Analytics"],
      technologies: ["CDN", "Video Encoding", "DRM", "CMS", "Adobe Creative Suite", "AWS Media Services"],
      href: "/industries/media"
    },
    { 
      icon: Zap, 
      title: "Energy", 
      category: "Energy & Utilities",
      description: "Optimize energy operations with smart grid solutions and renewable energy management systems.",
      features: ["Smart Grid Management", "Energy Trading", "Asset Management", "Regulatory Compliance"],
      technologies: ["SCADA", "IoT Sensors", "Blockchain", "AI/ML", "GIS", "Meter Data Management"],
      href: "/industries/energy"
    },
    { 
      icon: Car, 
      title: "Automotive", 
      category: "Automotive",
      description: "Drive innovation in automotive industry with connected vehicle solutions and manufacturing systems.",
      features: ["Connected Vehicles", "Fleet Management", "Manufacturing Execution", "Dealer Management"],
      technologies: ["IoT", "V2X Communication", "AUTOSAR", "CAN Bus", "OBD-II", "Telematics"],
      href: "/industries/automotive"
    },
    { 
      icon: GraduationCap, 
      title: "Education", 
      category: "Education",
      description: "Transform learning experiences with digital education platforms and student management systems.",
      features: ["Learning Management Systems", "Student Information Systems", "Online Assessment", "Virtual Classrooms"],
      technologies: ["Moodle", "Canvas", "Blackboard", "Zoom", "Microsoft Teams", "Google Workspace"],
      href: "/industries/education"
    },
    { 
      icon: Home, 
      title: "Real Estate", 
      category: "Real Estate",
      description: "Revolutionize property management and real estate transactions with innovative digital solutions.",
      features: ["Property Management", "CRM Systems", "Virtual Tours", "Market Analytics"],
      technologies: ["MLS Integration", "Zillow API", "Property Management Software", "VR/AR", "Blockchain"],
      href: "/industries/real-estate"
    },
    { 
      icon: Factory, 
      title: "Manufacturing", 
      category: "Manufacturing",
      description: "Optimize manufacturing processes with Industry 4.0 solutions and smart factory technologies.",
      features: ["Production Planning", "Quality Management", "Supply Chain Optimization", "Predictive Maintenance"],
      technologies: ["MES", "ERP", "IoT", "Digital Twin", "AI/ML", "Robotics"],
      href: "/industries/manufacturing"
    },
    { 
      icon: Truck, 
      title: "Logistics", 
      category: "Logistics & Transportation",
      description: "Streamline supply chain and logistics operations with intelligent tracking and management systems.",
      features: ["Fleet Tracking", "Route Optimization", "Warehouse Management", "Supply Chain Visibility"],
      technologies: ["GPS Tracking", "RFID", "Blockchain", "AI Optimization", "WMS", "TMS"],
      href: "/industries/logistics"
    },
    { 
      icon: Clapperboard, 
      title: "Entertainment", 
      category: "Entertainment",
      description: "Create immersive entertainment experiences with digital platforms and content delivery solutions.",
      features: ["Content Distribution", "User Engagement", "Monetization", "Rights Management"],
      technologies: ["Streaming Platforms", "DRM", "CDN", "Video Analytics", "Social Integration"],
      href: "/industries/entertainment"
    },
    { 
      icon: Package, 
      title: "Consumer Goods and Services", 
      category: "Consumer Goods",
      description: "Transform consumer product businesses with digital solutions for brand engagement and service delivery.",
      features: ["Product Lifecycle Management", "Brand Management", "Customer Experience", "Service Optimization"],
      technologies: ["PLM Systems", "CRM", "E-commerce Platforms", "Supply Chain", "Analytics", "Mobile Apps"],
      href: "/industries/consumer-goods"
    },
    { 
      icon: Power, 
      title: "Utilities", 
      category: "Utilities",
      description: "Modernize utility operations with smart infrastructure, billing systems, and customer service platforms.",
      features: ["Smart Grid Management", "Utility Billing", "Customer Information Systems", "Asset Management"],
      technologies: ["Smart Meters", "SCADA Systems", "GIS", "CIS", "Meter Data Management", "Grid Analytics"],
      href: "/industries/utilities"
    }
  ];

  const handleIndustryClick = (industry: Industry) => {
    navigate(industry.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="fixed top-16 left-0 right-0 bg-background border-b border-border shadow-xl z-[56]"
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Industries We Serve</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
          {industries.map((industry, index) => (
            <button
              key={index}
              onClick={() => handleIndustryClick(industry)}
              className="text-left px-4 py-3 text-sm text-foreground hover:text-orange-600 hover:bg-orange-50/50 transition-colors duration-200 rounded-md"
            >
              <div className="font-medium">{industry.title}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndustriesMenu;